import {
  type MarketResolutionThresholdPolicy,
  type MarketResolutionThresholdState,
  resolutionThresholdStateAt,
} from './resolution-threshold.js';

export type ConsensusThresholdInterpolation = 'step' | 'linear' | 'smoothstep' | 'monotone';

export interface ConsensusThresholdCurveInput {
  readonly policy: MarketResolutionThresholdPolicy;
  readonly tradingStartedAt: string;
  readonly resolvedAt: string | null;
  /** Display interpolation only; resolution rules still use the exact staircase. */
  readonly interpolation?: ConsensusThresholdInterpolation;
}

/** Unix seconds and a display fraction derived from exact D9 policy knots. */
export interface ConsensusThresholdPoint {
  readonly time: number;
  readonly value: number;
}

export interface ConsensusThresholdCurve {
  sample(seconds: number): number | null;
  series(fromSeconds: number, toSeconds: number): ReadonlyArray<ConsensusThresholdPoint> | null;
}

type ThresholdKnots = readonly [ConsensusThresholdPoint, ...ConsensusThresholdPoint[]];

interface ThresholdSegment {
  readonly start: ConsensusThresholdPoint;
  readonly end: ConsensusThresholdPoint;
  readonly startSlope: number;
  readonly endSlope: number;
}

// Schedule-aligned samples keep geometry stable across viewports and live-head updates.
const SAMPLES_PER_INTERVAL = 32;

/** Cache this display curve by policy/timestamps/mode when sampling animation frames. */
export function createConsensusThresholdCurve(
  input: ConsensusThresholdCurveInput,
): ConsensusThresholdCurve | null {
  const resolvedSeconds =
    input.resolvedAt === null ? Infinity : Date.parse(input.resolvedAt) / 1_000;
  if (input.resolvedAt !== null && !Number.isFinite(resolvedSeconds)) return null;
  const knots = thresholdKnots(input);
  if (knots === null) return null;
  const interpolation = input.interpolation ?? 'smoothstep';
  const segments = thresholdSegments(knots);
  const points = materializeCurve(knots, segments, interpolation);
  const sample = (seconds: number): number | null =>
    Number.isFinite(seconds)
      ? sampleCurve(knots, segments, Math.min(seconds, resolvedSeconds), interpolation)
      : null;

  return {
    sample,
    series(fromSeconds, toSeconds): ReadonlyArray<ConsensusThresholdPoint> | null {
      if (!Number.isFinite(fromSeconds) || !Number.isFinite(toSeconds)) return null;
      const endSeconds = Math.min(toSeconds, resolvedSeconds);
      if (endSeconds < fromSeconds) return null;
      const first = sample(fromSeconds);
      const last = sample(endSeconds);
      if (first === null || last === null) return null;
      const result = [
        { time: fromSeconds, value: first },
        ...points.filter((point) => point.time > fromSeconds && point.time <= endSeconds),
      ];
      if (result.at(-1)?.time !== endSeconds) {
        result.push({ time: endSeconds, value: last });
      }
      return result;
    },
  };
}

function thresholdKnots(input: ConsensusThresholdCurveInput): ThresholdKnots | null {
  const startSeconds = Math.floor(Date.parse(input.tradingStartedAt) / 1_000);
  if (input.policy.kind !== 'ready' || !Number.isFinite(startSeconds)) return null;
  const stateAt = (seconds: number): MarketResolutionThresholdState =>
    resolutionThresholdStateAt({
      policy: input.policy,
      status: 'trading',
      tradingStartedAt: input.tradingStartedAt,
      resolvedAt: null,
      nowTs: BigInt(Math.floor(seconds)),
    });
  let state = stateAt(startSeconds);
  if (state.kind !== 'ready') return null;
  const decayStart = startSeconds + Number(input.policy.policy.decayDelaySecs);
  if (!Number.isSafeInteger(decayStart)) return null;
  const knots: [ConsensusThresholdPoint, ...ConsensusThresholdPoint[]] = [
    { time: decayStart, value: state.thresholdFraction },
  ];
  while (state.nextDropAtMs !== null) {
    const seconds = state.nextDropAtMs / 1_000;
    if (!Number.isSafeInteger(seconds)) return null;
    const next = stateAt(seconds);
    if (next.kind !== 'ready' || next.thresholdPpb >= state.thresholdPpb) return null;
    knots.push({ time: seconds, value: next.thresholdFraction });
    state = next;
  }
  return knots;
}

function thresholdSegments(knots: ThresholdKnots): ReadonlyArray<ThresholdSegment> {
  let start = knots[0];
  return knots.slice(1).map((end, index) => {
    const segment = {
      start,
      end,
      startSlope: monotoneSlope(knots[index - 1], start, end),
      endSlope: monotoneSlope(start, end, knots[index + 2]),
    };
    start = end;
    return segment;
  });
}

function materializeCurve(
  knots: ThresholdKnots,
  segments: ReadonlyArray<ThresholdSegment>,
  interpolation: ConsensusThresholdInterpolation,
): ReadonlyArray<ConsensusThresholdPoint> {
  if (interpolation === 'linear') return knots;
  const points: ConsensusThresholdPoint[] = interpolation === 'step' ? [] : [knots[0]];
  for (const segment of segments) {
    const { start, end } = segment;
    if (interpolation === 'step') {
      points.push({ time: end.time, value: start.value }, end);
      continue;
    }
    for (let part = 1; part < SAMPLES_PER_INTERVAL; part += 1) {
      const time = start.time + ((end.time - start.time) * part) / SAMPLES_PER_INTERVAL;
      points.push({ time, value: interpolateSegment(segment, time, interpolation) });
    }
    points.push(end);
  }
  return points;
}

function sampleCurve(
  knots: ThresholdKnots,
  segments: ReadonlyArray<ThresholdSegment>,
  seconds: number,
  interpolation: ConsensusThresholdInterpolation,
): number {
  let value = knots[0].value;
  if (seconds <= knots[0].time) return value;
  for (const segment of segments) {
    if (seconds < segment.end.time) return interpolateSegment(segment, seconds, interpolation);
    value = segment.end.value;
  }
  return value;
}

function interpolateSegment(
  segment: ThresholdSegment,
  seconds: number,
  interpolation: ConsensusThresholdInterpolation,
): number {
  const { start, end } = segment;
  if (interpolation === 'step') return start.value;
  const duration = end.time - start.time;
  const u = (seconds - start.time) / duration;
  if (interpolation === 'linear') return start.value + (end.value - start.value) * u;
  const eased = u * u * (3 - 2 * u);
  const base = start.value + (end.value - start.value) * eased;
  if (interpolation === 'smoothstep') return base;
  const value =
    base +
    (u * u * u - 2 * u * u + u) * duration * segment.startSlope +
    (u * u * u - u * u) * duration * segment.endSlope;
  return Math.max(end.value, Math.min(start.value, value));
}

function monotoneSlope(
  previous: ConsensusThresholdPoint | undefined,
  current: ConsensusThresholdPoint,
  next: ConsensusThresholdPoint | undefined,
): number {
  // The first and last knots meet the constant delay and floor plateaus.
  if (previous === undefined || next === undefined) return 0;
  const leftWidth = current.time - previous.time;
  const rightWidth = next.time - current.time;
  const left = (current.value - previous.value) / leftWidth;
  const right = (next.value - current.value) / rightWidth;
  const weighted = (left * rightWidth + right * leftWidth) / (leftWidth + rightWidth);
  // Steffen's monotonicity limiter prevents cubic overshoot between policy knots.
  return (
    (Math.sign(left) + Math.sign(right)) *
    Math.min(Math.abs(left), Math.abs(right), Math.abs(weighted) / 2)
  );
}
